import { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

import AddDrug from "../../components/Drug/AddDrug";
import { __API_URL__ } from "../../SERVER_URL";
import DeleteMessage from "../../components/Modals/DeleteMessage";
import DrugMin from "../../components/Drug/DrugMin";

const AllDrugKits = ({
  setTypeOfMessage,
  setMessage,
  trial,
  manual_drug,
  unassign_drug_count,
  trialDetailApi,
}) => {
  const hiddenFileInput = useRef(null);

  const [drugKits, setDrugKits] = useState([]);
  const [addDrug, setAddDrug] = useState(false);
  const [deleteObj, setDeleteObj] = useState(false);
  const [miniDrug, setMiniDrug] = useState(false);
  const token = localStorage.getItem("token");
  const [pageItemCount, setPageItemCount] = useState(10);
  const [deleteObjid, setDeleteObjid] = useState(0);
  const [drugFile, setDrugFile] = useState("");
  const [newDrug, setNewDrug] = useState({ is_real_drug: true });

  const GetDrugKitsAPI = () => {
    axios
      .get(`${__API_URL__}/api/get_drug_kits/?trial=${trial}`, {
        headers: { Authorization: `Token ${token}` },
      })
      .then((res) => setDrugKits(res.data))
      .catch(() => {});
  };

  const handlePageClick = (page) => {
    axios
      .get(`${__API_URL__}/api/get_drug_kits/?page=${page}&trial=${trial}`, {
        headers: { Authorization: `Token ${token}` },
      })
      .then((res) => setDrugKits(res.data))
      .catch(() => {});
  };

  const handleOnclick = (id) => setDeleteObjid(id);

  const handleUploadFile = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) setDrugFile(selectedFile);
    if (hiddenFileInput.current) hiddenFileInput.current.value = "";
  };

  const confirmDelete = () => {
    if (deleteObjid) {
      axios
        .delete(`${__API_URL__}/api/delete_drug_kit/${deleteObjid}/`, {
          headers: { Authorization: `Token ${token}` },
        })
        .then(() => {
          setDeleteObj(false);
          GetDrugKitsAPI();
          toast.success("Drug Deleted Successfully!");
        })
        .catch((err) => {
          toast.error(err.response?.data?.message || "Delete failed");
        });
    }
  };

  const getSingleDrug = () => {
    axios
      .get(`${__API_URL__}/api/get_single_drug/${deleteObjid}/`, {
        headers: { Authorization: `Token ${token}` },
      })
      .then((res) => setNewDrug(res.data));
  };

  const UploadDrugFileApi = () => {
    const formData = new FormData();
    formData.append("filename", drugFile);
    formData.append("trail", trial);
    const requestOptions = {
      method: "POST",
      body: formData,
      headers: { Authorization: `Token ${token}` },
    };

    fetch(`${__API_URL__}/api/upload_drug_file/`, requestOptions)
      .then(() => {
        GetDrugKitsAPI();
        setDrugFile("");
        trialDetailApi();
        toast.success("Drug file uploaded Successfully!");
      })
      .catch((err) => {
        toast.error(err.response?.data?.message || "Upload failed");
      });
  };

  const handleClick = () => hiddenFileInput.current.click();

  useEffect(() => {
    GetDrugKitsAPI();
  }, []);

  useEffect(() => {
    if (drugFile) UploadDrugFileApi();
  }, [drugFile]);

  useEffect(() => {
    if (deleteObjid) {
      getSingleDrug();
    } else {
      setNewDrug({ is_real_drug: true });
    }
  }, [deleteObjid]);

  return (
    <Box>
      <Paper sx={{ mt: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2,
          }}
        >
          <Typography variant="h6">Drugs</Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button variant="contained" size="small" onClick={GetDrugKitsAPI}>
              Refresh
            </Button>
            <Button variant="contained" size="small" onClick={() => setMiniDrug(true)}>
              Drug Value (min-value)
            </Button>
            <Button variant="contained" size="small" onClick={handleClick}>
              Upload csv
            </Button>
            <input
              type="file"
              style={{ display: "none" }}
              accept=".csv"
              onChange={handleUploadFile}
              ref={hiddenFileInput}
            />
            <Button variant="contained" size="small" onClick={() => setAddDrug(true)}>
              Add Drug
            </Button>
          </Stack>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ px: 2, pb: 1 }}>
          Remaining Drug kits: {unassign_drug_count}
        </Typography>

        <TableContainer sx={{ maxHeight: "50vh" }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Serial Number</TableCell>
                <TableCell>Units/Dosage</TableCell>
                <TableCell>Real Drug</TableCell>
                <TableCell>Kit Id</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Subject Id</TableCell>
                <TableCell>Created at</TableCell>
                <TableCell>Delete</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {drugKits?.results?.map((data, index) => (
                <TableRow key={index} hover>
                  <TableCell sx={{ whiteSpace: "pre-wrap" }}>{data?.name}</TableCell>
                  <TableCell sx={{ whiteSpace: "pre-wrap" }}>{data?.serial_number}</TableCell>
                  <TableCell>{data?.dosage}</TableCell>
                  <TableCell>{data?.is_real_drug ? "True" : "False"}</TableCell>
                  <TableCell>{data?.custom_id || "-"}</TableCell>
                  <TableCell>{data?.patient || "-"}</TableCell>
                  <TableCell>{data?.participant_number || "-"}</TableCell>
                  <TableCell>
                    {new Date(data.created_at + "Z").toDateString()},{" "}
                    {new Date(data.created_at + "Z").toLocaleTimeString()}
                  </TableCell>
                  <TableCell>
                    {!data.patient && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          handleOnclick(data.id);
                          setDeleteObj(true);
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => {
                        handleOnclick(data.id);
                        setAddDrug(true);
                        getSingleDrug();
                      }}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {drugKits?.count > pageItemCount && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <Pagination
              count={Math.ceil(drugKits?.count / pageItemCount)}
              onChange={(e, page) => handlePageClick(page)}
              color="primary"
            />
          </Box>
        )}
      </Paper>

      {addDrug && (
        <AddDrug
          addDrug={addDrug}
          setAddDrug={setAddDrug}
          setMessage={setMessage}
          setTypeOfMessage={setTypeOfMessage}
          trial={trial}
          GetDrugKitsAPI={GetDrugKitsAPI}
          setDeleteObjid={setDeleteObjid}
          deleteObjid={deleteObjid}
          newDrug={newDrug}
          setNewDrug={setNewDrug}
          manual_drug={manual_drug}
          trialDetailApi={trialDetailApi}
        />
      )}
      {miniDrug && (
        <DrugMin
          miniDrug={miniDrug}
          setMiniDrug={setMiniDrug}
          setMessage={setMessage}
          setTypeOfMessage={setTypeOfMessage}
          trial={trial}
        />
      )}
      {deleteObj && (
        <DeleteMessage
          deleteObj={deleteObj}
          setDeleteObj={setDeleteObj}
          confirmDelete={confirmDelete}
        />
      )}
    </Box>
  );
};

export default AllDrugKits;
